import React from "react";
import styled from "styled-components";

import { ExpectationV2 } from "../../../../types/engagementAssessmentV2Types";
import {
  Card,
  HorizontalLine,
  RowContainer,
} from "../../../../components/containers/styledComponents";
import { CompassParagraph } from "../../../../components/common/styledComponents";
import {
  EASE,
  popIn,
  reducedMotion,
} from "../../../../components/visuals/motion";
import {
  darkGreen,
  greyText,
  lightGreyOutline,
  lightGreyText,
} from "../../../../styles";
import { EvidenceChips, ExpandableJudgement } from "./assessmentAtoms";

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  margin: 0 -16px;
`;

const Cell = styled.div<{ $index: number }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  padding: 12px 16px;
  animation: ${popIn} 0.3s ${EASE} both;
  animation-delay: ${({ $index }): number => 60 + $index * 45}ms;
  ${reducedMotion}

  & + & {
    border-left: 1px solid ${lightGreyOutline};
  }
`;

const HeadLine = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const Index = styled.span`
  width: 18px;
  flex-shrink: 0;
  font-family: "ApercuMono", "Apercu", monospace;
  font-size: 11px;
  color: ${lightGreyText};
`;

const Name = styled.span<{ $open: boolean }>`
  font-family: "ApercuMedium";
  font-size: 14px;
  color: ${({ $open }): string => ($open ? lightGreyText : darkGreen)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Spacer = styled.span`
  flex: 1;
`;

const Muted = styled.span`
  flex-shrink: 0;
  font-family: "Apercu";
  font-size: 11px;
  color: ${lightGreyText};
  white-space: nowrap;
`;

const Indent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-left: 28px;
`;

const MONTHS =
  "January|February|March|April|May|June|July|August|September|October|November|December";

const SPAN_COUNT = "a|one|two|three|four|five|six|twelve|eighteen|\\d+";

const TIMEFRAME = new RegExp(
  `\\b(by|within|in|before|until|over|during)\\s+((?:the\\s+)?(?:end\\s+of\\s+(?:the\\s+)?(?:year|quarter|month)|next\\s+(?:\\w+\\s+)?(?:years?|months?|quarters?|weeks?)|(?:${SPAN_COUNT})\\s+(?:years?|months?|quarters?|weeks?)|(?:early|mid|late)[-\\s](?:${MONTHS}|20\\d{2})|(?:${MONTHS})(?:\\s+20\\d{2})?|[QH][1-4](?:\\s+20\\d{2})?|20\\d{2}))`,
  "i",
);

const capitalise = (s: string): string =>
  s.charAt(0).toUpperCase() + s.slice(1);

const pad = (n: number): string => String(n).padStart(2, "0");

const timeframeOf = (text: string): string | null => {
  const match = TIMEFRAME.exec(text);
  if (!match) return null;
  const phrase = match[2].replace(/^the\s+/i, "");
  const bare = new RegExp(`^(?:${SPAN_COUNT})\\s`, "i").test(phrase);
  return capitalise(bare ? `${match[1].toLowerCase()} ${phrase}` : phrase);
};

const MONTH_LIST = MONTHS.toLowerCase().split("|");
const NUMBER_WORDS: Record<string, number> = {
  a: 1,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  twelve: 12,
  eighteen: 18,
};
const UNIT_MONTHS: Record<string, number> = {
  week: 0.25,
  month: 1,
  quarter: 3,
  year: 12,
};
const PHASE: Record<string, number> = { early: 0.2, mid: 0.5, late: 0.85 };

// Months from `now` to a fractional month (0 = start of Jan, 12 = end of Dec).
const monthsUntil = (month: number, year: number | null, now: Date): number => {
  const current = now.getMonth() + now.getDate() / 31;
  const delta = month - current;
  if (year !== null) return delta + (year - now.getFullYear()) * 12;
  return delta < -0.5 ? delta + 12 : delta;
};

const countOf = (word: string | undefined): number => {
  if (word === undefined) return 1;
  if (word in NUMBER_WORDS) return NUMBER_WORDS[word];
  return Number(word) || 1;
};

const horizonOf = (label: string, now: Date): number => {
  const t = label.toLowerCase();
  const yearMatch = /20\d{2}/.exec(t);
  const year = yearMatch ? Number(yearMatch[0]) : null;
  const next =
    /\b(?:next|within|in|over|during)\s+(?:(\w+)\s+)?(week|month|quarter|year)/.exec(
      t,
    );
  const quarter = /\bq([1-4])\b/.exec(t);
  const half = /\bh([12])\b/.exec(t);
  const monthIndex = MONTH_LIST.findIndex((m) => t.includes(m));
  const phase = /(early|mid|late)/.exec(t);

  let months: number;
  if (next) {
    months = countOf(next[1]) * UNIT_MONTHS[next[2]];
  } else if (t.includes("end of") && t.includes("quarter")) {
    const current = now.getMonth() + now.getDate() / 31;
    months = 3 - (current % 3);
  } else if (t.includes("end of") && t.includes("month")) {
    months = 1 - now.getDate() / 31;
  } else if (quarter) {
    months = monthsUntil(Number(quarter[1]) * 3, year, now);
  } else if (half) {
    months = monthsUntil(Number(half[1]) * 6, year, now);
  } else if (monthIndex >= 0) {
    const offset = phase ? PHASE[phase[1]] : 0.5;
    months = monthsUntil(monthIndex + offset, year, now);
  } else {
    months = monthsUntil(12, year, now);
  }
  return Math.max(0.25, months);
};

const relative = (months: number): string => {
  if (months < 1) return "< 1 mo";
  if (months < 12) return `≈ ${Math.round(months)} mo`;
  return `≈ ${Number((months / 12).toFixed(1))} yr`;
};

const isDated = (row: ExpectationRow): row is DatedRow =>
  row.due !== null && row.months !== null;

export const ExpectationsPanel = ({
  expectations,
}: ExpectationsPanelProps): JSX.Element | null => {
  if (expectations.length === 0) return null;

  const now = new Date();
  const rows: ExpectationRow[] = expectations.map((exp, index) => {
    const due = timeframeOf(exp.expectation);
    return {
      ...exp,
      index,
      due,
      months: due === null ? null : horizonOf(due, now),
    };
  });

  const dated = rows.filter(isDated).sort((a, b) => a.months - b.months);
  const soonest = dated.length > 0 ? dated[0].months : null;

  return (
    <Card w={100} h="auto" m="0 20px 72px 20px">
      <RowContainer mt={0} mb={0} jc="space-between" ai="center">
        <CompassParagraph fontSize={16} bold color={darkGreen}>
          Expectations
        </CompassParagraph>
        <CompassParagraph fontSize={12} color={greyText}>
          {dated.length} of {rows.length} time-bound
          {soonest !== null && ` · next ${relative(soonest)}`}
        </CompassParagraph>
      </RowContainer>
      <HorizontalLine color={lightGreyOutline} marginTop={4} marginBottom={0} />

      <Grid>
        {rows.map((row) => (
          <Cell key={`${row.expectation}-${row.index}`} $index={row.index}>
            <HeadLine>
              <Index>{pad(row.index + 1)}</Index>
              <Name $open={row.due === null}>{row.due ?? "Open-ended"}</Name>
              <Spacer />
              {row.months !== null && <Muted>{relative(row.months)}</Muted>}
            </HeadLine>
            <Indent>
              <ExpandableJudgement text={row.expectation} maxLines={3} />
              {row.evidence_refs.length > 0 && (
                <EvidenceChips refs={row.evidence_refs} max={2} />
              )}
            </Indent>
          </Cell>
        ))}
      </Grid>
    </Card>
  );
};

type ExpectationsPanelProps = {
  expectations: ExpectationV2[];
};

interface ExpectationRow extends ExpectationV2 {
  index: number;
  due: string | null;
  months: number | null;
}

interface DatedRow extends ExpectationRow {
  due: string;
  months: number;
}
